import React, {useMemo} from 'react';
import clsx from 'clsx';
import type {InputForm, FormValidationError} from '@netx/core-components/Form';
import {
	composeClasses,
	styled,
	type WithClasses,
} from '@netx/core-components/Theme';
import type {CustomFormItemType, ICustomFormItem} from '../../types';
import {LegacyFormCustomFormItemCheckboxes} from './LegacyFormCustomFormItemCheckboxes';
import {LegacyFormCustomFormItemDate} from './LegacyFormCustomFormItemDate';
import {LegacyFormCustomFormItemMultiselect} from './LegacyFormCustomFormItemMultiselect';
import {LegacyFormCustomFormItemNumber} from './LegacyFormCustomFormItemNumber';
import {LegacyFormCustomFormItemRadios} from './LegacyFormCustomFormItemRadios';
import {CustomFormItemSection} from './CustomFormItemSection';
import {LegacyFormCustomFormItemSelect} from './LegacyFormCustomFormItemSelect';
import {LegacyFormCustomFormItemText} from './LegacyFormCustomFormItemText';
import {
	customFormItemClasses,
	getCustomFormItemUtilityClass,
	CUSTOM_FORM_ITEM_CLASS_NAME,
	type CustomFormItemClasses,
} from './customFormItemClasses';

const getItemComponent = <T extends CustomFormItemType>(type: T) => {
	switch (type) {
		case 'checkboxes':
			return LegacyFormCustomFormItemCheckboxes;
		case 'date':
			return LegacyFormCustomFormItemDate;
		case 'multiselect':
			return LegacyFormCustomFormItemMultiselect;
		case 'number':
			return LegacyFormCustomFormItemNumber;
		case 'radios':
			return LegacyFormCustomFormItemRadios;
		case 'section':
			return CustomFormItemSection;
		case 'select':
			return LegacyFormCustomFormItemSelect;
		case 'text':
		case 'textarea':
			return LegacyFormCustomFormItemText;
	}
};

type CustomFormItemOwnerState = WithClasses<CustomFormItemClasses, {}>;

const useUtilityClasses = (props: CustomFormItemOwnerState) => {
	const {classes} = props;
	const slots = {
		root: ['root'],
	};
	return composeClasses(slots, getCustomFormItemUtilityClass, classes);
};

export const LegacyFormCustomFormItemRoot = styled('div', {
	name: CUSTOM_FORM_ITEM_CLASS_NAME,
	slot: 'Root',
})(({theme}) => ({
	width: '100%',
	marginTop: theme.spacing(2),
}));

export type LegacyFormCustomFormItemProps = WithClasses<
	CustomFormItemClasses,
	{
		disabled?: boolean;
		error?: FormValidationError;
		item: ICustomFormItem;
		value: any;
		inputForm: InputForm;
	}
>;

export const LegacyFormCustomFormItemPrimitive = ({
	classes: _classes,
	className,
	disabled,
	item,
	inputForm,
	...props
}: LegacyFormCustomFormItemProps) => {
	const ownerState: CustomFormItemOwnerState = {
		classes: _classes,
		className,
	};

	const classes = useUtilityClasses(ownerState);

	const Component = useMemo(() => getItemComponent(item.type), [item.type]);

	if (!Component) return null;

	return (
		<LegacyFormCustomFormItemRoot className={clsx(classes.root, className)}>
			<Component
				disabled={disabled}
				form={inputForm}
				item={item as never}
				{...props}
			/>
		</LegacyFormCustomFormItemRoot>
	);
};

export const LegacyFormCustomFormItem = LegacyFormCustomFormItemPrimitive;

export default LegacyFormCustomFormItem;
